import React, { useState } from 'react';
import { 
  Search, Plus, Bell, User, LayoutDashboard, 
  Map as MapIcon, FileText, Settings, HelpCircle, 
  Maximize2, Info, Edit2, Upload, Trash2, X 
} from 'lucide-react';

const MeasroDashboard = () => {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unit, setUnit] = useState('sq ft'); // Original area switch logic

  // Mock data based on the HTML content
  const projectDetails = {
    id: "MEA-9928-XP42",
    address: "123 Maple Avenue, Springfield",
    area: 2450.75,
    lastUpdated: "Oct 24, 2023"
  };

  return (
    <div className="flex min-h-screen bg-[#f8f9fa] font-sans">
      {/* Top Strip */}
      <header className="fixed top-0 left-0 right-0 h-[70px] bg-gradient-to-r from-[#6a11cb] to-[#2575fc] text-white flex items-center justify-between px-8 z-[1000] shadow-lg">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-white text-[#6a11cb] rounded-lg flex items-center justify-center font-extrabold text-xl shadow-md">
            M
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Measro</h1>
            <p className="text-[10px] opacity-90 -mt-1 uppercase tracking-widest">Dashboard</p>
          </div>
        </div>

        {/* Search Bar - Centered */}
        <div className="absolute left-1/2 -translate-x-1/2 w-full max-w-[600px]">
          <div className="bg-white/20 backdrop-blur-md border-2 border-white/30 rounded-xl px-4 py-2 flex items-center gap-3 focus-within:bg-white/30 transition-all">
            <Search className="w-4 h-4 text-white/90" />
            <input 
              type="text" 
              placeholder="Search properties, projects or reports..." 
              className="bg-transparent border-none outline-none text-white placeholder:text-white/70 w-full font-medium"
            />
          </div>
        </div>

        <div className="flex items-center gap-5">
          <button 
            onClick={() => setIsModalOpen(true)}
            className="bg-black hover:bg-zinc-800 text-white px-5 py-2 rounded-lg font-semibold flex items-center gap-2 transition-all shadow-md active:scale-95"
          >
            <Plus className="w-4 h-4" /> Add Project
          </button>
          
          <div className="relative">
            <button onClick={() => setIsNotificationOpen(!isNotificationOpen)} className="relative p-2 hover:bg-white/10 rounded-lg transition-colors">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 bg-red-500 text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">3</span>
            </button>
          </div>

          <div 
            className="flex items-center gap-3 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full cursor-pointer transition-all"
            onClick={() => setIsProfileOpen(!isProfileOpen)}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-pink-300 to-orange-200 flex items-center justify-center text-[#6a11cb] font-bold">
              JD
            </div>
            <span className="text-sm font-semibold">John Doe</span>
          </div>
        </div>
      </header>

      {/* Sidebar - Compact */}
      <aside className="w-20 bg-white border-r border-gray-200 fixed top-[70px] bottom-0 left-0 flex flex-col py-4 z-[900]">
        {[
          { icon: LayoutDashboard, label: "Home", active: true },
          { icon: MapIcon, label: "Maps" },
          { icon: FileText, label: "Reports", badge: "2" },
          { icon: Settings, label: "Settings" }
        ].map((item, idx) => (
          <button key={idx} className={`flex flex-col items-center gap-1 py-4 px-2 mx-2 rounded-lg transition-all mb-1 ${item.active ? 'bg-gradient-to-br from-[#6a11cb] to-[#2575fc] text-white shadow-md' : 'text-gray-500 hover:bg-indigo-50 hover:text-[#6a11cb]'}`}>
            <item.icon className="w-5 h-5" />
            <span className="text-[10px] font-bold uppercase">{item.label}</span>
            {item.badge && <span className="absolute top-2 right-4 bg-red-500 text-white text-[8px] px-1 rounded-full">{item.badge}</span>}
          </button>
        ))}
      </aside>

      {/* Main Content Area */}
      <main className="ml-20 mr-[420px] mt-[70px] p-8 w-full">
        <div className="bg-white rounded-2xl border border-gray-200 h-[calc(100vh-140px)] flex items-center justify-center text-gray-400">
          <div className="text-center">
            <MapIcon className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p>Interactive Map Interface would load here...</p>
          </div>
        </div>
      </main>

      {/* Right Side Panel - Project Details */}
      <aside className="w-[420px] bg-white border-l border-gray-200 fixed top-[70px] bottom-0 right-0 p-6 overflow-y-auto z-[900]">
        <div className="flex items-start justify-between mb-4 border-b border-gray-100 pb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#6a11cb]" /> Project Details
            </h2>
          </div>
        </div>

        {/* Property ID Section */}
        <div className="mb-6">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Property ID</label>
          <div className="bg-indigo-50 border border-indigo-100 text-[#6a11cb] font-mono p-3 rounded-lg font-bold tracking-wider">
            {projectDetails.id}
          </div>
        </div>

        {/* Details Card */}
        <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-5 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <MapIcon className="w-4 h-4 text-[#6a11cb]" /> Site Metrics
            </h3>
            <button className="w-7 h-7 bg-[#6a11cb] text-white rounded-full flex items-center justify-center hover:bg-indigo-700 transition-all">
              <Info className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-white">
              <span className="text-sm text-gray-600 flex items-center gap-2">Address</span>
              <span className="text-sm font-semibold text-gray-800 text-right max-w-[200px] truncate">{projectDetails.address}</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600">Measured Area</span>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setUnit(unit === 'sq ft' ? 'sq m' : 'sq ft')}
                  className="w-7 h-7 border border-gray-300 rounded text-xs flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-all"
                >
                  <Maximize2 className="w-3 h-3" />
                </button>
                <span className="font-bold text-gray-800">{projectDetails.area} {unit}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-xl hover:border-[#6a11cb] hover:bg-indigo-50 transition-all group">
            <div className="w-10 h-10 bg-gradient-to-br from-[#6a11cb] to-[#2575fc] rounded-lg flex items-center justify-center text-white">
              <Upload className="w-5 h-5" />
            </div>
            <span className="text-xs font-bold text-gray-700">Upload Files</span>
          </button>
          
          <button className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-xl hover:border-[#6a11cb] hover:bg-indigo-50 transition-all group">
            <div className="w-10 h-10 bg-gradient-to-br from-[#6a11cb] to-[#2575fc] rounded-lg flex items-center justify-center text-white">
              <Edit2 className="w-5 h-5" />
            </div>
            <span className="text-xs font-bold text-gray-700">Add Instructions</span>
          </button>
        </div>
      </aside>

      {/* Floating Modal Placeholder */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[3000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="bg-gradient-to-r from-[#6a11cb] to-[#2575fc] p-6 text-white flex justify-between items-center">
              <h3 className="text-xl font-bold">New Measurement Project</h3>
              <button onClick={() => setIsModalOpen(false)}><X /></button>
            </div>
            <div className="p-8">
              <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">Project Name</label>
                <input type="text" className="w-full border border-gray-300 rounded-lg p-3 outline-none focus:border-[#6a11cb] focus:ring-2 focus:ring-indigo-100" placeholder="Enter name..." />
              </div>
              <div className="flex justify-end gap-3 mt-8">
                <button onClick={() => setIsModalOpen(false)} className="px-6 py-2 border border-gray-300 rounded-lg font-semibold text-gray-600">Cancel</button>
                <button className="px-6 py-2 bg-[#6a11cb] text-white rounded-lg font-semibold shadow-lg shadow-indigo-200">Create Project</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeasroDashboard;